import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";
import Select, {components as selectComponents} from "react-select";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import useAuthorityConfiguration from "../../api/utilities/useAuthorityConfiguration.js";
import {AdministrationApi} from "../../api/authority/generated/index.ts";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import Button from "../../components/Button.jsx";
import Badge from "../../components/Badge.jsx";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import Modal from "../../components/Modal.jsx";
import YesNo from "../../components/YesNo.jsx";
import {SortableHeader, useSortableRows} from "./ReportingLab.jsx";
import {countPublicSubscriptionPackages, createSubscriptionPackageDraft, fetchSubscriptionPackageAdminData, MAX_PUBLIC_SUBSCRIPTION_PACKAGES, sortSubscriptionPackagesDefault, subscriptionPackageSortValue, supportedPackageFeatures} from "./subscriptionPackagesData.js";
import {getTranslatedPlanName} from "../project/Subscription/PlanName.jsx";

const monthlyPriceFormatter = new Intl.NumberFormat("cs-CZ");
const modalSelectStyles = {menuPortal: (base) => ({...base, zIndex: 9999})};

function ProjectValueContainer({children, ...props}) {
    const selectedCount = props.getValue().length;

    return <selectComponents.ValueContainer {...props}>
        {selectedCount > 0 && props.selectProps.inputValue === ""
            ? <span className="truncate text-slate-600 dark:text-slate-200">{props.selectProps.selectedSummary}</span>
            : children[0]}
        {children[1]}
    </selectComponents.ValueContainer>;
}

const projectSelectComponents = {ValueContainer: ProjectValueContainer};

function ProjectNames({projects}) {
    if (!projects?.length) return <span className="text-slate-400">—</span>;

    const visibleProjects = projects.slice(0, 3);
    const remainingCount = projects.length - visibleProjects.length;

    return <span title={projects.join(", ")}>
        {visibleProjects.join(", ")}
        {remainingCount > 0 && <span className="ml-1 font-medium text-slate-500">+{remainingCount}</span>}
    </span>;
}

function PackageAdminName({code, fallbackName, t}) {
    const translatedName = getTranslatedPlanName(t, code);
    const packageCode = code ?? fallbackName;

    if (!translatedName) {
        return packageCode;
    }

    return <>
        {translatedName}
        <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">({packageCode})</span>
    </>;
}

export default function SubscriptionPackages() {
    const {t} = useTranslation();
    const {et} = useErrorTranslation();
    const configuration = useAuthorityConfiguration();
    const [packages, setPackages] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [duplicatingCode, setDuplicatingCode] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deletingPackage, setDeletingPackage] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [search, setSearch] = useState("");
    const [publicFilter, setPublicFilter] = useState("all");
    const [usage, setUsage] = useState("all");
    const [catalog, setCatalog] = useState("all");
    const [showArchived, setShowArchived] = useState(false);
    const loadFailedMessage = t("admin.subscription_packages.load_failed", {defaultValue: "Balíčky se nepodařilo načíst."});
    const loadDependencies = useRef({configuration, et, loadFailedMessage});
    loadDependencies.current = {configuration, et, loadFailedMessage};
    const limitFields = [
        ["maxDomains", t("admin.subscription_packages.max_domains", {defaultValue: "Max. domén"})],
        ["dataUpdatesPerDay", t("admin.subscription_packages.data_updates_per_day", {defaultValue: "Aktualizací denně"})],
        ["retentionMonths", t("admin.subscription_packages.retention_months", {defaultValue: "Retence (měsíce, 0 = bez limitu)"})],
    ];
    const priceFields = [
        ["monthlyCzk", t("admin.subscription_packages.monthly_czk", {defaultValue: "Měsíčně CZK"})],
        ["yearlyCzk", t("admin.subscription_packages.yearly_czk", {defaultValue: "Měsíční sazba při roční platbě CZK"})],
        ["monthlyEur", t("admin.subscription_packages.monthly_eur", {defaultValue: "Měsíčně EUR"})],
        ["yearlyEur", t("admin.subscription_packages.yearly_eur", {defaultValue: "Měsíční sazba při roční platbě EUR"})],
    ];
    const availabilityFields = [
        ["public", t("admin.subscription_packages.public", {defaultValue: "Veřejný balíček"})],
        ["isArchived", t("admin.subscription_packages.archived", {defaultValue: "Archivní"})],
    ];
    const load = useCallback(async () => {
        const {configuration: currentConfiguration, et: translateError, loadFailedMessage: currentLoadFailedMessage} = loadDependencies.current;
        setLoading(true);
        try {
            const result = await fetchSubscriptionPackageAdminData(new AdministrationApi(currentConfiguration));
            setPackages(result.packages);
            setProjects(result.projects);
        } catch (error) {
            translateError(error).then((message) => toast.error(message ?? currentLoadFailedMessage));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    const filteredPackages = useMemo(() => packages.filter((item) => {
        if (!showArchived && item.isArchived) return false;
        if (publicFilter === "public" && !item.public) return false;
        if (publicFilter === "private" && item.public) return false;
        if (usage === "used" && item.usageCount === 0) return false;
        if (usage === "unused" && item.usageCount > 0) return false;
        if (catalog === "legacy" && !item.isLegacy) return false;
        if (catalog === "new" && item.isLegacy) return false;
        const needle = search.trim().toLocaleLowerCase("cs");
        return needle === "" || [item.code, item.name, item.internalNote, ...(item.currentProjects ?? []), ...(item.nextProjects ?? []), ...(item.availableForProjects ?? []).map((project) => project.name)]
            .some((value) => String(value ?? "").toLocaleLowerCase("cs").includes(needle));
    }), [catalog, packages, publicFilter, search, showArchived, usage]);
    const defaultSortedPackages = useMemo(() => sortSubscriptionPackagesDefault(filteredPackages), [filteredPackages]);
    const publicPackageLimitReached = countPublicSubscriptionPackages(packages) >= MAX_PUBLIC_SUBSCRIPTION_PACKAGES;
    const {sortedRows: filtered, sort: tableSort, toggleSort, resetSort} = useSortableRows(defaultSortedPackages, "defaultOrder", subscriptionPackageSortValue);
    const filtersOrSortingChanged = search !== ""
        || publicFilter !== "all"
        || usage !== "all"
        || catalog !== "all"
        || showArchived
        || tableSort.key !== "defaultOrder"
        || tableSort.direction !== "desc";

    function resetFiltersAndSorting() {
        setSearch("");
        setPublicFilter("all");
        setUsage("all");
        setCatalog("all");
        setShowArchived(false);
        resetSort();
    }

    function beginEdit(item) {
        setDuplicatingCode(null);
        setEditingId(item.id);
        setDraft({
            ...item,
            code: item.code ?? "",
            features: supportedPackageFeatures(item),
            availableProjectIds: (item.availableForProjects ?? []).map((project) => project.id),
        });
    }

    function beginCreate() {
        setDuplicatingCode(null);
        setEditingId("new");
        setDraft(createSubscriptionPackageDraft());
    }

    function beginDuplicate(item) {
        setDuplicatingCode(item.code ?? item.name ?? "");
        setEditingId("new");
        setDraft(createSubscriptionPackageDraft(item, !publicPackageLimitReached));
    }

    function closeEditor() {
        setDuplicatingCode(null);
        setEditingId(null);
        setDraft(null);
    }

    async function save() {
        if (draft.isArchived && draft.public) {
            toast.error(t("admin.subscription_packages.archived_invalid", {defaultValue: "Archivní balíček nemůže být veřejný."}));
            return;
        }
        setSaving(true);
        try {
            const api = new AdministrationApi(configuration);
            const features = supportedPackageFeatures(draft);
            const payload = {
                ...draft,
                _public: draft.public,
                allowDiscountCodeEvaluation: false,
                allowInfluencerEvaluation: false,
                allowAffiliateEvaluation: features.additional_cost_sources,
                features,
            };
            if (editingId === "new") {
                await api.createAdminSubscriptionPackage({subscriptionPackageUpdateData: payload});
            } else {
                await api.updateAdminSubscriptionPackage({
                    packageId: draft.id,
                    subscriptionPackageUpdateData: payload,
                });
            }
            toast.success(t("admin.subscription_packages.saved", {defaultValue: "Balíček byl uložen."}));
            closeEditor();
            await load();
        } catch (error) {
            et(error).then((message) => toast.error(message ?? t("admin.subscription_packages.save_failed", {defaultValue: "Balíček se nepodařilo uložit."})));
        } finally {
            setSaving(false);
        }
    }

    async function removePackage() {
        if (!deletingPackage?.canDelete) return;

        setDeleting(true);
        try {
            await new AdministrationApi(configuration).deleteAdminSubscriptionPackage({packageId: deletingPackage.id});
            toast.success(t("admin.subscription_packages.deleted", {defaultValue: "Balíček byl smazán."}));
            setDeletingPackage(null);
            await load();
        } catch (error) {
            et(error).then((message) => toast.error(message ?? t("admin.subscription_packages.delete_failed", {defaultValue: "Balíček se nepodařilo smazat."})));
        } finally {
            setDeleting(false);
        }
    }

    return <WideSecureLayout
        title={t("admin.subscription_packages.title", {defaultValue: "Balíčky"})}
        actions={<Button onClick={beginCreate}>{t("admin.subscription_packages.create", {defaultValue: "Nový balíček"})}</Button>}
    >
        <div className="space-y-5">
            <div className="app-surface grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800 md:grid-cols-5">
                <input className="form-input w-full md:col-span-5" placeholder={t("admin.subscription_packages.search", {defaultValue: "Kód, poznámka nebo projekt…"})} value={search} onChange={(event) => setSearch(event.target.value)}/>
                <select className="form-select w-full" value={publicFilter} onChange={(event) => setPublicFilter(event.target.value)}>
                    <option value="all">{t("admin.subscription_packages.public_filter_all", {defaultValue: "Veřejné i neveřejné"})}</option><option value="public">{t("admin.subscription_packages.public_filter_public", {defaultValue: "Pouze veřejné"})}</option><option value="private">{t("admin.subscription_packages.public_filter_private", {defaultValue: "Pouze neveřejné"})}</option>
                </select>
                <select className="form-select w-full" value={usage} onChange={(event) => setUsage(event.target.value)}>
                    <option value="all">{t("admin.subscription_packages.usage_all", {defaultValue: "Používané i nepoužívané"})}</option><option value="used">{t("admin.subscription_packages.usage_used", {defaultValue: "Pouze používané"})}</option><option value="unused">{t("admin.subscription_packages.usage_unused", {defaultValue: "Pouze nepoužívané"})}</option>
                </select>
                <select className="form-select w-full" value={catalog} onChange={(event) => setCatalog(event.target.value)}>
                    <option value="all">{t("admin.subscription_packages.catalog_all", {defaultValue: "Starý i nový model"})}</option><option value="legacy">{t("admin.subscription_packages.catalog_legacy_only", {defaultValue: "Pouze starý model"})}</option><option value="new">{t("admin.subscription_packages.catalog_new_only", {defaultValue: "Pouze nový model"})}</option>
                </select>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)}/> {t("admin.subscription_packages.show_archived", {defaultValue: "Zobrazit archivní"})}</label>
                <Button variant={filtersOrSortingChanged ? "black" : "outline-primary"} disabled={!filtersOrSortingChanged} onClick={resetFiltersAndSorting}>
                    {t("admin.subscription_packages.reset_filters_sorting", {defaultValue: "Vymazat filtry a řazení"})}
                </Button>
            </div>

            <LoadingComponent loading={loading}>
                <div className="app-surface overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
                            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-900/40 dark:text-slate-400">
                                <tr>
                                    <SortableHeader label={t("admin.subscription_packages.package", {defaultValue: "Balíček"})} sortKey="package" sort={tableSort} onSort={toggleSort} className="px-5 py-3"/>
                                    <SortableHeader label={t("admin.subscription_packages.monthly_price", {defaultValue: "Měsíční cena"})} sortKey="monthlyPrice" sort={tableSort} onSort={toggleSort} className="px-5 py-3"/>
                                    <SortableHeader label={t("admin.subscription_packages.usage", {defaultValue: "Využití"})} sortKey="usage" sort={tableSort} onSort={toggleSort} className="px-5 py-3"/>
                                    <SortableHeader label={t("admin.subscription_packages.assigned_projects", {defaultValue: "Přiřazené projekty"})} sortKey="projects" sort={tableSort} onSort={toggleSort} className="px-5 py-3"/>
                                    <th className="w-24 px-5 py-3"><span className="sr-only">{t("actions")}</span></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/70">
                                {filtered.map((item) => <tr key={item.id} className={`transition-colors ${item.isLegacy
                                    ? "odd:bg-amber-50/70 even:bg-amber-100/60 hover:!bg-amber-100 dark:odd:bg-amber-900/10 dark:even:bg-amber-900/20 dark:hover:!bg-amber-900/30"
                                    : "odd:bg-white even:bg-slate-50 hover:!bg-violet-50 dark:odd:bg-slate-800 dark:even:bg-slate-900/30 dark:hover:!bg-violet-950/30"}`}>
                                        <td className="px-5 py-4 align-top">
                                            <div className="font-semibold text-slate-900 dark:text-white">
                                                <PackageAdminName code={item.code} fallbackName={item.name} t={t}/>
                                            </div>
                                            <div className="mt-0.5 font-mono text-[11px] text-slate-400 dark:text-slate-500" title={item.id}>
                                                ID: {item.id}
                                            </div>
                                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                                                <Badge color={item.isArchived ? "slate" : item.public ? "emerald" : "blue"}>{item.isArchived ? t("admin.subscription_packages.archived", {defaultValue: "Archivní"}) : item.public ? t("admin.subscription_packages.public_short", {defaultValue: "Veřejný"}) : t("admin.subscription_packages.private", {defaultValue: "Neveřejný"})}</Badge>
                                                {item.isLegacy && <Badge color="yellow">{t("admin.subscription_packages.catalog_legacy", {defaultValue: "Starý model"})}</Badge>}
                                            </div>
                                            {item.internalNote && <div className="mt-2 max-w-72 whitespace-pre-wrap text-xs text-slate-500 dark:text-slate-400">{item.internalNote}</div>}
                                        </td>
                                        <td className="whitespace-nowrap px-5 py-4 align-top">
                                            <div className="text-base font-semibold text-slate-900 dark:text-white">{monthlyPriceFormatter.format(item.monthlyCzk)} Kč</div>
                                            <div className="mt-0.5 text-xs text-slate-500">{monthlyPriceFormatter.format(item.monthlyEur)} €</div>
                                        </td>
                                        <td className="px-5 py-4 align-top">
                                            <div className="font-medium text-slate-800 dark:text-slate-200">{item.usageCount > 0 ? t("admin.subscription_packages.project_count", {defaultValue: "{{count}} projektů", count: item.usageCount}) : t("admin.subscription_packages.unused", {defaultValue: "Nepoužívaný"})}</div>
                                            {item.nextProjects?.length > 0 && <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">{t("admin.subscription_packages.scheduled_count", {defaultValue: "{{count}} naplánováno", count: item.nextProjects.length})}</div>}
                                        </td>
                                        <td className="min-w-72 px-5 py-4 align-top text-xs leading-5 text-slate-600 dark:text-slate-300">
                                            <ProjectNames projects={item.currentProjects}/>
                                            {item.nextProjects?.length > 0 && <div className="mt-1 text-slate-500"><span className="font-medium">{t("admin.subscription_packages.next", {defaultValue: "Následující"})}:</span> <ProjectNames projects={item.nextProjects}/></div>}
                                            <div className="mt-1 text-slate-500"><span className="font-medium">{t("admin.subscription_packages.available", {defaultValue: "Dostupný"})}:</span> {item.availableForProjects?.length ? <ProjectNames projects={item.availableForProjects.map((project) => project.name)}/> : t("admin.subscription_packages.everyone", {defaultValue: "všem projektům"})}</div>
                                        </td>
                                        <td className="px-5 py-4 align-top">
                                            <div className="flex justify-end gap-2">
                                                {!item.isLegacy && <Button size="small" variant="outline-primary" onClick={() => beginDuplicate(item)}>
                                                    {t("admin.subscription_packages.duplicate", {defaultValue: "Duplikovat"})}
                                                </Button>}
                                                {item.canDelete && <Button size="small" variant="outline-red" onClick={() => setDeletingPackage(item)}>
                                                    {t("admin.subscription_packages.delete", {defaultValue: "Smazat"})}
                                                </Button>}
                                                <Button size="small" variant="outline-primary" onClick={() => beginEdit(item)}>{t("admin.subscription_packages.edit")}</Button>
                                            </div>
                                        </td>
                                    </tr>)}
                                {!loading && filtered.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500">{t("admin.subscription_packages.empty", {defaultValue: "Filtrům neodpovídá žádný balíček."})}</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            </LoadingComponent>
            <Modal
                className="max-w-7xl my-auto"
                visible={draft !== null}
                setVisible={(visible) => { if (!visible && !saving) closeEditor(); }}
                title={duplicatingCode !== null
                    ? t("admin.subscription_packages.duplicate_title", {defaultValue: "Duplikovat balíček"})
                    : editingId === "new"
                        ? t("admin.subscription_packages.create")
                        : t("admin.subscription_packages.edit_title", {defaultValue: "Upravit balíček"})}
                subtitle={duplicatingCode ?? (editingId === "new" ? null : draft?.code ?? draft?.name)}
                loading={saving}
            >
                {draft && <PackageForm
                    key={`${editingId}-${duplicatingCode ?? ""}`}
                    draft={draft}
                    setDraft={setDraft}
                    projects={projects}
                    limitFields={limitFields}
                    priceFields={priceFields}
                    availabilityFields={availabilityFields}
                    publicSelectionDisabled={!draft.public
                        && publicPackageLimitReached
                        && (editingId === "new" || packages.find((item) => item.id === editingId)?.public !== true)}
                    t={t}
                    saving={saving}
                    onCancel={closeEditor}
                    onSave={() => void save()}
                />}
            </Modal>
            <YesNo
                visible={deletingPackage !== null}
                setVisible={(visible) => { if (!visible && !deleting) setDeletingPackage(null); }}
                title={t("admin.subscription_packages.delete_title", {defaultValue: "Smazat balíček"})}
                question={t("admin.subscription_packages.delete_question", {
                    defaultValue: "Opravdu chcete trvale smazat balíček {{name}}? Tuto akci nelze vrátit zpět.",
                    name: deletingPackage?.code ?? deletingPackage?.name ?? "",
                })}
                onYes={() => void removePackage()}
                loading={deleting}
            />
        </div>
    </WideSecureLayout>;
}

function PackageForm({draft, setDraft, projects, limitFields, priceFields, availabilityFields, publicSelectionDisabled, t, saving, onCancel, onSave}) {
    const [projectSearch, setProjectSearch] = useState("");
    const projectOptions = useMemo(
        () => projects.map((project) => ({value: project.id, label: project.name})),
        [projects],
    );
    const selectedProjectIds = useMemo(() => new Set(draft.availableProjectIds ?? []), [draft.availableProjectIds]);
    const selectedProjects = useMemo(
        () => projectOptions.filter((project) => selectedProjectIds.has(project.value)),
        [projectOptions, selectedProjectIds],
    );
    const hasProjectQuery = projectSearch.trim().length >= 2;
    const setFeature = (key, value) => setDraft({...draft, features: {...(draft.features ?? {}), [key]: value}});
    const unsetFeature = (key) => {
        const features = {...(draft.features ?? {})};
        delete features[key];
        setDraft({...draft, features});
    };

    return <>
        <div className="space-y-5">
            <FormSection title={t("admin.subscription_packages.basic_settings", {defaultValue: "Nastavení balíčku"})}>
                {draft.isLegacy
                    ? <div className="app-surface mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                        <span className="font-semibold">{t("admin.subscription_packages.catalog_type", {defaultValue: "Model balíčku"})}:</span>{" "}
                        {t("admin.subscription_packages.catalog_legacy", {defaultValue: "Starý model"})}
                        <span className="ml-2 opacity-80">{t("admin.subscription_packages.catalog_immutable", {defaultValue: "Typ je neměnný."})}</span>
                    </div>
                    : null}
                <div className="grid gap-4 md:grid-cols-4">
                    <label className="md:col-span-4 text-sm">{t("admin.subscription_packages.code", {defaultValue: "Kód balíčku"})}<input className="form-input mt-1 w-full" value={draft.code ?? ""} onChange={(event) => setDraft({...draft, code: event.target.value})} placeholder={t("admin.subscription_packages.code_placeholder", {defaultValue: "Např. growing_eshop nebo klient_custom"})}/></label>
                    {priceFields.map(([field, label]) => <label className="text-sm" key={field}>{label}<input type="number" min="0" className="form-input mt-1 w-full" value={draft[field]} onChange={(event) => setDraft({...draft, [field]: Number(event.target.value)})}/></label>)}
                    <label className="md:col-span-4 text-sm">{t("admin.subscription_packages.internal_note", {defaultValue: "Interní poznámka"})}<textarea className="form-textarea mt-1 min-h-20 w-full" value={draft.internalNote ?? ""} onChange={(event) => setDraft({...draft, internalNote: event.target.value})} placeholder={t("admin.subscription_packages.internal_note_placeholder", {defaultValue: "Pro koho je balíček určený, důvod výjimky, historie ceny…"})}/></label>
                    <label className="md:col-span-2 text-sm">{t("admin.subscription_packages.feature_support", {defaultValue: "Podpora"})}<select className="form-select mt-1 w-full" value={draft.features?.support ?? "email"} onChange={(event) => setFeature("support", event.target.value)}><option value="email">{t("admin.subscription_packages.support_email", {defaultValue: "E-mail"})}</option><option value="email_chat">{t("admin.subscription_packages.support_email_chat", {defaultValue: "E-mail a chat"})}</option><option value="priority_sla">{t("admin.subscription_packages.support_priority_sla", {defaultValue: "Prioritní SLA"})}</option></select></label>
                    <label className="md:col-span-2 text-sm">{t("admin.subscription_packages.feature_consulting", {defaultValue: "Konzultace v ceně"})}<select className="form-select mt-1 w-full" value={draft.features?.included_consulting ?? ""} onChange={(event) => event.target.value === "" ? unsetFeature("included_consulting") : setFeature("included_consulting", event.target.value)}><option value="">{t("admin.subscription_packages.consulting_none", {defaultValue: "Bez konzultací"})}</option><option value="1h_quarter">{t("admin.subscription_packages.consulting_quarterly", {defaultValue: "1 hodina za čtvrtletí"})}</option></select></label>
                </div>
            </FormSection>

            <FormSection title={t("admin.subscription_packages.availability_settings", {defaultValue: "Dostupnost balíčku"})}>
                <div className="text-sm">
                    <span>{t("admin.subscription_packages.available_projects", {defaultValue: "Dostupný pro projekty"})}</span>
                    {selectedProjects.length > 0 && <div className="mt-2 flex flex-wrap gap-2">
                        {selectedProjects.map((project) => <span key={project.value} className="inline-flex items-center gap-2 rounded-full bg-violet-100 px-3 py-1 text-sm font-medium text-violet-800 dark:bg-violet-900/30 dark:text-violet-200">
                            {project.label}
                            <button
                                type="button"
                                className="text-violet-500 hover:text-violet-900 dark:text-violet-300 dark:hover:text-white"
                                title={project.label}
                                onClick={() => setDraft({
                                    ...draft,
                                    availableProjectIds: draft.availableProjectIds.filter((projectId) => projectId !== project.value),
                                })}
                            >
                                ×
                            </button>
                        </span>)}
                    </div>}
                    <Select
                        isMulti className="datixo_select_container mt-1" classNamePrefix="datixo_select"
                        options={hasProjectQuery ? projectOptions : []} value={selectedProjects} inputValue={projectSearch}
                        controlShouldRenderValue={false} closeMenuOnSelect={false} hideSelectedOptions
                        components={projectSelectComponents} menuPortalTarget={document.body} menuPosition="fixed"
                        styles={modalSelectStyles}
                        selectedSummary={t("admin.subscription_packages.selected_projects", {defaultValue: "Vybráno projektů: {{count}}", count: selectedProjects.length})}
                        placeholder={t("admin.subscription_packages.project_search", {defaultValue: "Napište alespoň 2 znaky názvu projektu…"})}
                        noOptionsMessage={() => hasProjectQuery ? t("admin.subscription_packages.no_projects_found", {defaultValue: "Žádný projekt nebyl nalezen."}) : t("admin.subscription_packages.project_search_minimum", {defaultValue: "Pro vyhledání napište alespoň 2 znaky."})}
                        onInputChange={(value, meta) => { if (meta.action === "input-change") setProjectSearch(value); }}
                        onChange={(values) => setDraft({...draft, availableProjectIds: values.map((project) => project.value)})}
                    />
                    <span className="mt-1 block text-xs text-slate-500">{t("admin.subscription_packages.available_projects_help", {defaultValue: "Veřejný balíček je dostupný všem. Neveřejný balíček je dostupný pouze vybraným projektům."})}</span>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-3">{availabilityFields.map(([field, label]) => <BooleanField key={field} label={label} checked={draft[field]} disabled={field === "public" && publicSelectionDisabled} onChange={(checked) => setDraft({...draft, [field]: checked})}/>)}</div>
                {publicSelectionDisabled && <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">{t("admin.subscription_packages.public_limit_reached", {defaultValue: "Veřejný katalog už obsahuje maximální počet 3 balíčků. Nejdřív zneveřejněte některý stávající balíček."})}</p>}
            </FormSection>

            <FormSection title={t("admin.subscription_packages.limits", {defaultValue: "Limity"})}>
                <div className="grid gap-4 md:grid-cols-4">
                    <FeatureNumber
                        label={t("admin.subscription_packages.feature_marketing_integrations", {defaultValue: "Marketingové integrace (-1 = neomezeně)"})}
                        value={draft.features?.max_marketing_integrations ?? draft.maxIntegrations}
                        onChange={(value) => setDraft({
                            ...draft,
                            maxIntegrations: value,
                            features: {...(draft.features ?? {}), max_marketing_integrations: value},
                        })}
                    />
                    {limitFields.map(([field, label]) => <label className="text-sm" key={field}>{label}<input type="number" min="0" className="form-input mt-1 w-full" value={draft[field]} onChange={(event) => setDraft({...draft, [field]: Number(event.target.value)})}/></label>)}
                </div>
            </FormSection>

            <FormSection
                title={t("admin.subscription_packages.feature_settings", {defaultValue: "Funkce balíčku"})}
                description={t("admin.subscription_packages.feature_settings_help", {defaultValue: "Nastavovat lze pouze funkce podporované aplikací."})}
            >
                <div className="grid gap-4 md:grid-cols-2">
                    {[
                        ["additional_cost_sources", t("admin.subscription_packages.feature_additional_cost_sources", {defaultValue: "E-mailing, affiliate a ostatní náklady"})],
                        ["abc_analysis", t("admin.subscription_packages.feature_abc_analysis", {defaultValue: "ABC analýza produktů"})],
                        ["mcp_access", t("admin.subscription_packages.feature_mcp_access", {defaultValue: "MCP přístup pro AI"})],
                        ["roles_permissions", t("admin.subscription_packages.feature_roles_permissions", {defaultValue: "Role a oprávnění"})],
                    ].map(([key, label]) => <BooleanField key={key} label={label} checked={draft.features?.[key] === true} onChange={(checked) => setFeature(key, checked)}/>)}
                </div>
                <div className="my-5 border-t border-slate-300 dark:border-slate-600"/>
                <div className="grid gap-4 md:grid-cols-2">
                    <FeatureSelect
                        label={t("admin.subscription_packages.feature_marketing_performance", {defaultValue: "Marketingový výkon"})}
                        value={draft.features?.marketing_performance ?? "channel_campaign"}
                        options={[
                            ["channel_campaign", t("admin.subscription_packages.marketing_performance_channel_campaign", {defaultValue: "Kanál / kampaň"})],
                            ["campaign_adset_ad", t("admin.subscription_packages.marketing_performance_campaign_adset_ad", {defaultValue: "Kampaň / sada / reklama"})],
                        ]}
                        onChange={(value) => setFeature("marketing_performance", value)}
                    />
                    <FeatureSelect
                        label={t("admin.subscription_packages.feature_onboarding", {defaultValue: "Onboarding"})}
                        value={draft.features?.onboarding ?? "self_service_review"}
                        options={[
                            ["self_service_review", t("admin.subscription_packages.onboarding_self_service_review", {defaultValue: "Self-service s kontrolou"})],
                            ["validation_included", t("admin.subscription_packages.onboarding_validation_included", {defaultValue: "Validační onboarding v ceně"})],
                            ["personal", t("admin.subscription_packages.onboarding_personal", {defaultValue: "Osobní onboarding"})],
                        ]}
                        onChange={(value) => setFeature("onboarding", value)}
                    />
                    <FeatureSelect
                        label={t("admin.subscription_packages.feature_setup_support", {defaultValue: "Podpora k nastavení aplikace"})}
                        value={String(draft.features?.setup_support_minutes ?? 0)}
                        options={[
                            ["0", t("admin.subscription_packages.setup_support_none", {defaultValue: "Bez podpory zdarma"})],
                            ["30", t("admin.subscription_packages.setup_support_30", {defaultValue: "Do 30 minut zdarma"})],
                            ["60", t("admin.subscription_packages.setup_support_60", {defaultValue: "Do 60 minut zdarma"})],
                        ]}
                        onChange={(value) => setFeature("setup_support_minutes", Number(value))}
                    />
                    <FeatureSelect
                        label={t("admin.subscription_packages.feature_team_training", {defaultValue: "Školení týmu"})}
                        value={draft.features?.team_training ?? "video_docs"}
                        options={[
                            ["video_docs", t("admin.subscription_packages.team_training_video_docs", {defaultValue: "Dokumentace"})],
                            ["online_training_video_docs", t("admin.subscription_packages.team_training_online_training_video_docs", {defaultValue: "1× online školení a dokumentace"})],
                        ]}
                        onChange={(value) => setFeature("team_training", value)}
                    />
                </div>
            </FormSection>
        </div>
        <div className="mt-5 flex justify-end gap-2"><Button variant="outline-primary" onClick={onCancel}>{t("admin.subscription_packages.cancel")}</Button><Button disabled={saving || String(draft.code ?? "").trim() === ""} onClick={onSave}>{saving ? t("admin.subscription_packages.saving", {defaultValue: "Ukládám…"}) : t("save")}</Button></div>
    </>;
}

function FormSection({title, description = null, children}) {
    return <section className="app-surface rounded-xl border-2 border-slate-300 bg-slate-50/70 p-4 dark:border-slate-600 dark:bg-slate-900/30">
        <div className="mb-4">
            <h3 className="font-semibold text-slate-900 dark:text-white">{title}</h3>
            {description && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{description}</p>}
        </div>
        {children}
    </section>;
}

function BooleanField({label, checked, disabled = false, onChange}) {
    return <label className={`flex items-center gap-2 text-sm ${disabled ? "cursor-not-allowed opacity-60" : ""}`}><input type="checkbox" checked={Boolean(checked)} disabled={disabled} onChange={(event) => onChange(event.target.checked)}/>{label}</label>;
}

function FeatureNumber({label, value, onChange}) {
    return <label className="text-sm">{label}<input type="number" className="form-input mt-1 w-full" value={value} onChange={(event) => onChange(Number(event.target.value))}/></label>;
}

function FeatureSelect({label, value, options, onChange}) {
    const selectOptions = options.map(([optionValue, optionLabel]) => ({value: optionValue, label: optionLabel}));

    return <label className="text-sm">
        {label}
        <Select
            className="datixo_select_container mt-1"
            classNamePrefix="datixo_select"
            options={selectOptions}
            value={selectOptions.find((option) => option.value === value)}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={modalSelectStyles}
            isSearchable={false}
            onChange={(option) => onChange(option.value)}
        />
    </label>;
}
